import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Download, Mail, Send } from "lucide-react";
import { api, fileUrl, LOGO_URL } from "../lib/api";
import { Layout } from "../components/Layout";
import { BiniBubble } from "../components/Bini";
import { t } from "../i18n";

const inputCls = "w-full rounded-[20px] border-4 border-[#38B6FF]/25 bg-white px-5 py-3 text-lg outline-none focus:border-[#38B6FF]";
const longDate = (iso) => new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
const detail = (e) => (typeof e.response?.data?.detail === "string" ? e.response.data.detail : t("shop.error"));

const useNoIndex = () => {
  useEffect(() => {
    const m = document.createElement("meta");
    m.name = "robots";
    m.content = "noindex, nofollow";
    document.head.appendChild(m);
    return () => m.remove();
  }, []);
};

const BackBtn = () => (
  <Link to="/espace-parents" data-testid="payment-back-btn" className="btn-3d no-shine mt-6 w-full" style={{ "--c": "#38B6FF" }}>{t("shop.back")}</Link>
);

const ShopCard = ({ testId, step, children }) => (
  <Layout back="/espace-parents">
    <div className="cloud-card puffs mx-auto mt-10 max-w-xl p-8" data-testid={testId} data-step={step}>
      <img src={LOGO_URL} alt="MIZUBINI" data-testid="shop-logo" className="mx-auto mb-6 h-24 w-24 rounded-[26px] border-4 border-white object-cover shadow-lg" />
      {children}
      <BackBtn />
    </div>
  </Layout>
);

const DownloadButtons = ({ token, order }) => (
  <>
    <a href={fileUrl(`/download/${token}`)} data-testid="payment-download-btn" className="btn-3d no-shine mt-5 w-full !text-lg" style={{ "--c": "#4CC417" }}>
      <Download className="w-6 h-6 shrink-0" strokeWidth={2.5} /> {t("shop.downloadMine")}
    </a>
    {order.bonus && (
      <a href={fileUrl(`/download/${token}/bonus`)} data-testid="payment-bonus-download-btn" className="btn-3d no-shine mt-4 w-full !text-lg" style={{ "--c": "#F0303A" }}>
        <Download className="w-6 h-6 shrink-0" strokeWidth={2.5} /> {t("shop.downloadBonus", { title: order.bonus.title })}
      </a>
    )}
    {order.bonus && <p className="mt-2 text-sm opacity-70" data-testid="payment-bonus-hint">{t("shop.bonusHint")}</p>}
    <p className="mt-3 text-sm opacity-70" data-testid="payment-valid-until">{t("shop.validUntil", { date: longDate(order.expires_at) })}</p>
  </>
);

export const LostLinkForm = () => {
  const [email, setEmail] = useState("");
  const [website, setWebsite] = useState("");
  const [state, setState] = useState("idle");
  const submit = async (e) => {
    e.preventDefault();
    setState("busy");
    try { await api.post("/payments/lost-link", { email, website }); setState("done"); }
    catch (err) { toast.error(detail(err)); setState("idle"); }
  };
  if (state === "done") return <p className="mt-4 font-bold text-[#3a9a10]" data-testid="lost-link-done">{t("shop.lostDone")}</p>;
  return (
    <form onSubmit={submit} className="mt-4 space-y-4" data-testid="lost-link-form">
      <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("shop.lostEmail")}
        aria-label={t("shop.lostEmail")} className={inputCls} data-testid="lost-link-email-input" />
      <input type="text" tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} className="hidden" aria-hidden="true" />
      <button type="submit" disabled={state === "busy"} className="btn-3d w-full disabled:opacity-60" style={{ "--c": "#8A3FE0" }} data-testid="lost-link-submit-btn">
        <Send className="w-5 h-5" /> {t("shop.lostSubmit")}
      </button>
    </form>
  );
};

const ResendButton = ({ sessionId, onSent }) => {
  const [busy, setBusy] = useState(false);
  const resend = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/payments/order/${sessionId}/resend`); toast.success(t("shop.resent", { email: data.email })); onSent(); }
    catch (e) { toast.error(detail(e)); }
    setBusy(false);
  };
  return (
    <button type="button" onClick={resend} disabled={busy} data-testid="payment-resend-btn"
      className="btn-3d is-off no-shine mt-4 w-full !min-h-[52px] !text-base disabled:opacity-60">
      <Mail className="w-5 h-5" /> {t("shop.resend")}
    </button>
  );
};

export function PaymentSuccess() {
  useNoIndex();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ step: "checking" });
  const [reload, setReload] = useState(0);

  useEffect(() => {
    if (!sessionId) return setState({ step: "failed" });
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          const { data: order } = await api.get(`/payments/order/${sessionId}`);
          setState({ step: order.expired ? "expired" : "paid", order });
          if (order.email_status !== "sending" || tries > 6) return;
        } else if (data.payment_status === "processing") {
          setState({ step: "processing" });
        } else if (["expired", "failed", "refunded"].includes(data.payment_status)) return setState({ step: "failed" });
      } catch { /* retry */ }
      if (++tries >= 15) return setState((s) => (s.step === "checking" ? { step: "failed" } : s));
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, reload]);

  const { step, order } = state;
  return (
    <ShopCard testId="payment-success-page" step={step}>
      {step === "checking" && <BiniBubble testId="payment-checking">{t("shop.checking")}</BiniBubble>}
      {step === "processing" && <BiniBubble testId="payment-processing">{t("shop.processing")}</BiniBubble>}
      {(step === "paid" || step === "expired") && (
        <>
          <BiniBubble testId="payment-paid" mood="happy">{t("shop.thanks")}</BiniBubble>
          <h2 className="mt-6 text-2xl font-extrabold" data-testid="payment-pack-title">{order.title}</h2>
          {step === "paid" ? <DownloadButtons token={order.download_token} order={order} />
            : <p className="mt-3 font-semibold" data-testid="payment-expired">{t("shop.expired")}</p>}
          {order.email_status === "sent" && (
            <p className="mt-3 flex items-center gap-2 text-sm font-semibold text-[#3a9a10]" data-testid="payment-email-sent">
              <Mail className="h-4 w-4 shrink-0" /> {t("shop.emailSent", { email: order.email })}
            </p>
          )}
          {order.email && <ResendButton sessionId={sessionId} onSent={() => setReload((n) => n + 1)} />}
        </>
      )}
      {step === "failed" && <BiniBubble testId="payment-failed">{t("shop.failed")}</BiniBubble>}
    </ShopCard>
  );
}

export function DownloadPage() {
  useNoIndex();
  const { token } = useParams();
  const [state, setState] = useState({ step: "checking" });
  useEffect(() => {
    api.get(`/download/${token}/info`)
      .then(({ data }) => setState({ step: data.expired ? "expired" : "ready", order: data }))
      .catch(() => setState({ step: "invalid" }));
  }, [token]);
  const { step, order } = state;
  return (
    <ShopCard testId="download-page" step={step}>
      {step === "checking" && <BiniBubble testId="download-checking">{t("shop.checkingLink")}</BiniBubble>}
      {step === "ready" && (
        <>
          <BiniBubble testId="download-ready" mood="happy">{t("shop.thanks")}</BiniBubble>
          <h2 className="mt-6 text-2xl font-extrabold" data-testid="download-pack-title">{order.title}</h2>
          <DownloadButtons token={token} order={order} />
        </>
      )}
      {(step === "expired" || step === "invalid") && (
        <>
          <BiniBubble testId={`download-${step}`}>{t(step === "expired" ? "shop.expired" : "shop.invalidLink")}</BiniBubble>
          <p className="mt-6 font-semibold">{t("shop.lostHint")}</p>
          <LostLinkForm />
        </>
      )}
    </ShopCard>
  );
}

export function LostLinkPage() {
  useNoIndex();
  return (
    <ShopCard testId="lost-link-page">
      <h1 className="text-3xl font-extrabold">{t("shop.lostTitle")}</h1>
      <p className="mt-2 opacity-80">{t("shop.lostHint")}</p>
      <LostLinkForm />
    </ShopCard>
  );
}

export function PaymentCancel() {
  return (
    <ShopCard testId="payment-cancel-page">
      <BiniBubble testId="payment-cancel">{t("shop.cancel")}</BiniBubble>
    </ShopCard>
  );
}
